import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { FaCarAlt, FaCalculator } from 'react-icons/fa';
import { getBrands, getModelsByBrand } from '../services/vehicleDataService';

const DealerVehicleForm = ({ isActive, formData, handleChange, handlePrev, handleNext }) => {
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const [calculatedPayment, setCalculatedPayment] = useState(0);

    // Vade seçenekleri
    const termOptions = [
        { value: 12, label: '12 Ay' },
        { value: 18, label: '18 Ay' },
        { value: 24, label: '24 Ay' },
        { value: 36, label: '36 Ay' }
    ];

    // Faiz oranları
    const getInterestRate = (months) => {
        if (months <= 12) return 0.07;      // %7
        if (months <= 18) return 0.0675;    // %6.75
        return 0.065;                        // %6.5
    };

    // PMT hesaplama
    const calculatePMT = (principal, months, rate) => {
        if (principal <= 0 || months <= 0) return 0;
        const payment = (principal * (rate * Math.pow((1 + rate), months))) / (Math.pow((1 + rate), months) - 1);
        return Math.round(payment);
    };

    // Aylık ödeme hesapla
    useEffect(() => {
        const vehicleAmount = Number(formData.vehicleAmount) || 0;
        const downPayment = Number(formData.downPayment) || 0;
        const term = Number(formData.term) || 36;

        const loanAmount = vehicleAmount - downPayment;
        const rate = getInterestRate(term);
        const payment = calculatePMT(loanAmount, term, rate);

        setCalculatedPayment(payment);

        // formData'ya monthlyPayment ekle
        if (payment > 0 && payment !== Number(formData.monthlyPayment)) {
            handleChange({
                target: {
                    name: 'monthlyPayment',
                    value: payment
                }
            });
        }
    }, [formData.vehicleAmount, formData.downPayment, formData.term]);

    // Component aktif olduğunda markaları yükle
    useEffect(() => {
        if (isActive) {
            loadBrands();
        }
    }, [isActive]);

    // Marka değiştiğinde modelleri yükle
    useEffect(() => {
        if (formData.preferredVehicleBrand) {
            loadModels(formData.preferredVehicleBrand);
        } else {
            setModels([]);
            if (formData.preferredVehicleModel) {
                handleChange({
                    target: {
                        name: 'preferredVehicleModel',
                        value: ''
                    }
                });
            }
        }
    }, [formData.preferredVehicleBrand]);

    const loadBrands = async () => {
        try {
            setLoadingBrands(true);
            const response = await getBrands();
            if (response.success) {
                setBrands(response.data || []);
            } else {
                setBrands([]);
            }
        } catch (error) {
            console.error('Error loading brands:', error);
            setBrands([]);
        } finally {
            setLoadingBrands(false);
        }
    };

    const loadModels = async (brandName) => {
        try {
            setLoadingModels(true);
            const response = await getModelsByBrand(brandName);
            if (response.success) {
                setModels(response.data || []);
            } else {
                setModels([]);
            }
        } catch (error) {
            console.error('Error loading models:', error);
            setModels([]);
        } finally {
            setLoadingModels(false);
        }
    };

    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear; year >= currentYear - 25; year--) {
            years.push(year);
        }
        return years;
    };

    const formatCurrency = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    if (!isActive) return null;

    const vehicleAmount = Number(formData.vehicleAmount) || 0;
    const downPayment = Number(formData.downPayment) || 0;
    const loanAmount = vehicleAmount - downPayment;

    return (
        <div id="arac-form">
            <h4 className="form-section-title">
                <FaCarAlt className="me-2" /> Araç ve Finansman Bilgileri
            </h4>

            <Row className="mb-4">
                {/* Araç Markası */}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Tercih Ettiğiniz Araç Markası</Form.Label>
                        <Form.Select
                            name="preferredVehicleBrand"
                            value={formData.preferredVehicleBrand}
                            onChange={handleChange}
                            disabled={loadingBrands}
                        >
                            <option value="">
                                {loadingBrands ? 'Markalar yükleniyor...' : 'Marka seçiniz'}
                            </option>
                            {brands.map(brand => (
                                <option key={brand} value={brand}>{brand}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>

                {/* Araç Modeli */}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Tercih Ettiğiniz Araç Modeli</Form.Label>
                        <Form.Select
                            name="preferredVehicleModel"
                            value={formData.preferredVehicleModel}
                            onChange={handleChange}
                            disabled={!formData.preferredVehicleBrand || loadingModels}
                        >
                            <option value="">
                                {!formData.preferredVehicleBrand ? 'Önce marka seçiniz' :
                                    loadingModels ? 'Modeller yükleniyor...' : 'Model seçiniz'}
                            </option>
                            {models.map(model => (
                                <option key={model.name} value={model.name}>{model.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>

                {/* Model Yılı */}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Model Yılı</Form.Label>
                        <Form.Select
                            name="vehicleYear"
                            value={formData.vehicleYear}
                            onChange={handleChange}
                        >
                            <option value="">Yıl seçiniz</option>
                            {getYearOptions().map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>

                {/* Araç Bedeli */}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Araç Bedeli (₺) <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="number"
                            name="vehicleAmount"
                            value={formData.vehicleAmount}
                            onChange={handleChange}
                            placeholder="Araç bedelini giriniz"
                            min="0"
                            step="1000"
                            required
                        />
                    </Form.Group>
                </Col>

                {/* Peşinat */}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Peşinat (₺)</Form.Label>
                        <Form.Control
                            type="number"
                            name="downPayment"
                            value={formData.downPayment}
                            onChange={handleChange}
                            placeholder="Peşinat tutarı"
                            min="0"
                            step="1000"
                        />
                    </Form.Group>
                </Col>

                {/* Vade Sayısı */}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Vade Sayısı <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                            name="term"
                            value={formData.term}
                            onChange={handleChange}
                            required
                        >
                            {termOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {/* Hesaplama Özeti */}
            {vehicleAmount > 0 && (
                <Alert variant="info" className="mb-4">
                    <h6 className="mb-3">
                        <FaCalculator className="me-2" />
                        Finansman Özeti
                    </h6>
                    <Row>
                        <Col md={3}>
                            <small className="text-muted">Araç Bedeli</small>
                            <div><strong>₺{formatCurrency(vehicleAmount)}</strong></div>
                        </Col>
                        <Col md={3}>
                            <small className="text-muted">Peşinat</small>
                            <div><strong>₺{formatCurrency(downPayment)}</strong></div>
                        </Col>
                        <Col md={3}>
                            <small className="text-muted">Kredi Tutarı</small>
                            <div><strong>₺{formatCurrency(loanAmount)}</strong></div>
                        </Col>
                        <Col md={3}>
                            <small className="text-muted">Aylık Taksit</small>
                            <div><strong className="text-primary fs-5">₺{formatCurrency(calculatedPayment)}</strong></div>
                        </Col>
                    </Row>
                </Alert>
            )}

            <div className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={handlePrev}>
                    Geri Dön
                </Button>
                <Button variant="primary" onClick={handleNext}>
                    Devam Et
                </Button>
            </div>
        </div>
    );
};

export default DealerVehicleForm;